import React from 'react'
import PropTypes from 'prop-types'
import { Box } from 'theme-ui'
import buildResponsiveVariant from '@components/utils/buildResponsiveVariant'
import hashCode from '@components/utils/hashCode'
import reduceArray from '@components/utils/reduceArray'
import Section from '@components/Section'
import CardListScroller from './CardList.Scroller'
import Card from '@components/Card'

const SLIDER_VARIANT_GROUP = 'lists.cards.slider'
const FIXED_VARIANT_GROUP = 'lists.cards.fixed'

const CardList = React.forwardRef((props, ref) => {
  const {
    nodes,
    variant,
    title,
    withTitleLink,
    limit,
    skip,
    distinct,
    slider,
    aside,
    asNavFor,
    loading,
    columns,
    /* Autoplay is opt-in and passed through to the scroller, which owns the
       pause/resume logic and the reduced-motion handling. Dwell time in ms. */
    autoplay,
    autoplayLabels,
    // Slider-only options kept in the call sites for compatibility. The old
    // react-slick carousel consumed these; nothing does now, and they must not
    // be spread onto the DOM node as attributes.
    autoPlay,
    autoplaySpeed,
    fade,
    dots,
    arrows,
    controlPosition,
    smoothAutoScroll,
    slidesToScroll,
    beforeChange,
    centerMode,
    centerPadding,
    ...rest
  } = props

  const reducedNodes = reduceArray(nodes, { distinct, limit, skip })
  if (!reducedNodes || !reducedNodes.length) return null

  //Section title link for viewing more posts from same category
  const titleLink = withTitleLink
    ? reducedNodes[0].category && reducedNodes[0].category.slug
    : ''

  //Unique key for section
  const sectionKey =
    title && `${hashCode(reducedNodes.map(node => node.id).join())}`

  //Build responsive variant for card list
  const cardListVariant = buildResponsiveVariant(
    slider ? SLIDER_VARIANT_GROUP : FIXED_VARIANT_GROUP,
    variant
  )

  /**
   * Horizontal nav used to drive a react-slick instance. The carousel is now a
   * CSS scroll-snap scroller, so there is no slick API to call; hovering a card
   * simply does nothing instead of scrolling the hero underneath the pointer.
   */
  const changeSlide = () => {}

  //Array of cards
  const cards = reducedNodes.map((node, index) => (
    <Card
      key={node.id}
      variant={variant}
      //Only the first card is above the fold in the scroller
      loading={index === 0 ? loading : undefined}
      {...node}
      {...rest}
    />
  ))

  /**
   * For a fixed (non-slider) list, `columns` used to be dead: only the slick
   * carousel consumed it, and the fixed layout relies on flex-wrap where every
   * `.blog_card` has `flexGrow: 1` and no basis - so each card stretches to the
   * full row and a "3 column" list renders as three full-width cards stacked
   * vertically. That is what happened to the homepage recap row after the
   * carousel was replaced.
   *
   * An explicit grid makes `columns` mean what it says, per breakpoint.
   */
  const columnsArr = columns
    ? (Array.isArray(columns) ? columns : [columns])
    : null
  const gridSx =
    !slider && columnsArr
      ? {
          display: 'grid',
          gap: 3,
          gridTemplateColumns: columnsArr.map(c =>
            // 0 is the historical "same as previous" marker in the call sites.
            `repeat(${c || 1}, minmax(0, 1fr))`
          )
        }
      : null

  //Cards List (Fixed or Slider)
  const CardList = () => (
    <Box sx={{ ...(gridSx || { variant: cardListVariant }) }}>
      {slider ? (
        // Named from the section title when there is one, so the label follows
        // the page language instead of being hard-coded in this component.
        <CardListScroller
          ref={ref}
          columns={columns}
          ariaLabel={title || undefined}
          autoplay={autoplay}
          autoplayLabels={autoplayLabels}
          {...rest}
        >
          {cards}
        </CardListScroller>
      ) : (
        cards
      )}
    </Box>
  )

  return title ? (
    <Section title={title} titleLink={titleLink} key={sectionKey} aside={aside}>
      <CardList />
    </Section>
  ) : (
    <CardList />
  )
})

export default CardList

CardList.defaultProps = {
  variant: 'vertical',
  columns: [1],
  aside: false
}

CardList.propTypes = {
  variant: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.oneOf([
      'horizontal',
      'horizontal-aside',
      'horizontal-md',
      'horizontal-lg',
      'horizontal-cover',
      'horizontal-cover-hero',
      'horizontal-cover-wide',
      'horizontal-hero',
      'vertical-cover',
      'vertical',
      'search'
    ])
  ]),
  loading: PropTypes.oneOf(['lazy', 'auto', 'eager']),
  columns: PropTypes.array,
  title: PropTypes.string,
  withTitleLink: PropTypes.bool,
  nodes: PropTypes.array,
  distinct: PropTypes.bool,
  limit: PropTypes.number,
  skip: PropTypes.number,
  aside: PropTypes.bool
}
